import axios from "axios";
import { getStorageName } from "../../helpers/storageName/storageName.ts";
import { generateCorrelationId } from "../../helpers/functions/generateCorrelationId.ts";
import { getRevmaticsCookie } from "../../helpers/cookie/revmaticsCookies.ts";
import { getCookieName } from "../../helpers/cookieName/cookieName.ts";
import { displayAlert } from "../slice/alertSlice.tsx";
import {
  redirectToSignIn,
  clearRedirectCounter,
} from "../../auth/redirectToSignIn";

export const instance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use(
  (config) => {
    const cookieBrand = getRevmaticsCookie(getCookieName.currentBrand);
    const cookieOrg = getRevmaticsCookie(getCookieName.currentOrganization);
    const token = localStorage.getItem(getStorageName.accessToken);
    const storedBrand = localStorage.getItem(getStorageName.currentBrand);
    const storedOrganization = localStorage.getItem(
      getStorageName.currentOrganization,
    );

    const currentBrand = cookieBrand || storedBrand;
    const currentOrganization = cookieOrg || storedOrganization;

    const isInternalApi =
      config.url?.startsWith("/api/") ||
      config.url?.startsWith(import.meta.env.VITE_BASE_URL + "/api/");

    if (isInternalApi) {
      config.headers["X-Correlation-ID"] = generateCorrelationId();
    }

    if (token) {
      // Context headers are nested here so stale cross-app cookies
      // never pollute requests made before authentication is established.
      config.headers["Authorization"] = `Bearer ${token}`;
      if (!config.headers["X-Organization-ID"] && currentOrganization) {
        config.headers["X-Organization-ID"] = currentOrganization;
      }
      if (!config.headers["X-Brand-ID"] && currentBrand) {
        config.headers["X-Brand-ID"] = currentBrand;
      }
    } else {
      delete config.headers["Authorization"];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (response) => {
    clearRedirectCounter();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      let message = "Too many requests. Please slow down and try again.";
      if (retryAfter) {
        const seconds = parseInt(retryAfter, 10);
        if (!isNaN(seconds)) {
          message = `Too many requests. Please try again in ${seconds} second${seconds !== 1 ? "s" : ""}.`;
        } else {
          const retryDate = new Date(retryAfter);
          if (!isNaN(retryDate.getTime())) {
            const wait = Math.ceil((retryDate.getTime() - Date.now()) / 1000);
            if (wait > 0) {
              message = `Too many requests. Please try again in ${wait} second${wait !== 1 ? "s" : ""}.`;
            }
          }
        }
      }
      const { store } = await import("../root.ts");
      store.dispatch(displayAlert({ type: "error", text: message }));
      return Promise.reject(error);
    }

    const refresh = localStorage.getItem(getStorageName.refreshToken);

    if (error.response.status === 401) {
      const accessToken = localStorage.getItem(getStorageName.accessToken);

      // No tokens at all — user was never authenticated, not session-expired.
      // Just reject; let the UI handle the unauthenticated state without redirecting.
      if (!accessToken && !refresh) {
        return Promise.reject(error);
      }

      const expiredHeading = "Your session has expired.";
      const expiredText = "Please log in again to continue.";
      const expiredMessage = { heading: expiredHeading, text: expiredText };

      const handleExpiredError = () => {
        localStorage.removeItem(getStorageName.accessToken);
        localStorage.removeItem(getStorageName.refreshToken);
        sessionStorage.setItem("expired_err", JSON.stringify(expiredMessage));
        redirectToSignIn();
      };

      if (
        originalRequest.url !== "/api/token/" &&
        originalRequest.url !== "/api/token/refresh/" &&
        originalRequest.url !== "/api/auth/sso-cookie/" &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (!refresh) {
          error.response.data.detail = expiredHeading;
          handleExpiredError();
          return Promise.reject(error);
        }

        try {
          const response = await instance.post("/api/token/refresh/", {
            refresh,
          });
          localStorage.setItem(
            getStorageName.accessToken,
            response.data.access,
          );
          localStorage.setItem(
            getStorageName.refreshToken,
            response.data.refresh,
          );

          instance.defaults.headers.common["Authorization"] =
            "Bearer " + response.data.access;
          return instance(originalRequest);
        } catch (_error) {
          if (axios.isAxiosError(_error) && _error.response)
            _error.response.data.detail = expiredHeading;
          handleExpiredError();
          return Promise.reject(_error);
        }
      }
    }

    return Promise.reject(error);
  },
);
